import React, { FormEventHandler } from "react";
import { useFormContext } from "react-hook-form";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import Button from "components/Button";

import { AmountTabs } from "./AmountTabs";
import FormSkeleton from "./FormSkeleton";
import AccountSelect from "./AccountSelect";
import { AccountFields } from "./AccountFields";
import { AdditionalSettings } from "./AdditionalSettings";
import PaymentPurposeField from "./PaymentPurposeField";

import { IbanAccount } from "store/features/ibanAccountSlice";
import { constructBankAccountOptions } from "shared/functions/ibanQrHelpers";

import { styles } from "./styles";

interface FormProps {
  accounts?: IbanAccount[];
  onSubmit: FormEventHandler<HTMLFormElement>;
  onSelectAccount: (account: IbanAccount) => void;
  loading: boolean;
  dataLoading: boolean;
}

const Form: React.FC<FormProps> = (props) => {
  const { accounts, onSubmit, onSelectAccount, loading, dataLoading } = props;
  const formContext = useFormContext();

  const options = constructBankAccountOptions(accounts);

  const ibanAccountUid = formContext.watch("ibanAccountUid");

  const isNewAccount = !!accounts?.[0] && ibanAccountUid === "new";

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      const target = event.target as HTMLElement;

      if (target.tagName !== "TEXTAREA" && target.tagName !== "BUTTON") {
        event.preventDefault();
      }
    }
  };

  if (dataLoading) {
    return <FormSkeleton />;
  }

  return (
    <Box
      component="form"
      id="invoice-create-form"
      onSubmit={onSubmit}
      noValidate
      onKeyDown={handleKeyDown}
      sx={styles.form}
    >
      <Box sx={styles.formContent}>
        <Typography variant="h3" sx={{ mb: 0.5 }}>
          Заповніть форму
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }} color="grey700">
          Для генерування посилання на оплату необхідно внести реквізити
        </Typography>

        {options.length > 0 && (
          <AccountSelect options={options} onSelectAccount={onSelectAccount} />
        )}

        {(options.length <= 0 || isNewAccount) && (
          <AccountFields isNewAccount={isNewAccount} />
        )}

        <AmountTabs />

        <PaymentPurposeField />

        <AdditionalSettings />
      </Box>
      <Box sx={styles.buttonWrapper}>
        <Button
          data-testid="create-invoice-button"
          variant="contained"
          color="primary"
          fullWidth
          type="submit"
          loading={loading}
        >
          Створити посилання
        </Button>
      </Box>
    </Box>
  );
};

export default Form;
